import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  CreditUsagePoint, EmployeePerformancePoint, LeadFunnelPoint, ReportKpis, ReportName, RevenueByServicePoint, RevenueMonthlyPoint,
} from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';
import { getAccessToken } from '@/lib/supabase';

export interface ReportRange {
  from?: string;
  to?: string;
}

function query(range: ReportRange, locationId?: string): Record<string, string | undefined> {
  return { from: range.from, to: range.to, locationId };
}

const REPORTS_STALE_TIME = 5 * 60_000;

export function useReportKpis(workspaceId: string | null, range: ReportRange, locationId?: string) {
  return useQuery({
    queryKey: [workspaceId, 'reports', 'kpis', range, locationId],
    queryFn: () => api.get<ReportKpis>(workspacePath(workspaceId!, '/reports/kpis'), { query: query(range, locationId) }),
    enabled: !!workspaceId,
    staleTime: REPORTS_STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useReportRevenueMonthly(workspaceId: string | null, range: ReportRange, locationId?: string) {
  return useQuery({
    queryKey: [workspaceId, 'reports', 'revenue-monthly', range, locationId],
    queryFn: () => api.get<RevenueMonthlyPoint[]>(workspacePath(workspaceId!, '/reports/revenue-monthly'), { query: query(range, locationId) }),
    enabled: !!workspaceId,
    staleTime: REPORTS_STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useReportRevenueByService(workspaceId: string | null, range: ReportRange, locationId?: string) {
  return useQuery({
    queryKey: [workspaceId, 'reports', 'revenue-by-service', range, locationId],
    queryFn: () => api.get<RevenueByServicePoint[]>(workspacePath(workspaceId!, '/reports/revenue-by-service'), { query: query(range, locationId) }),
    enabled: !!workspaceId,
    staleTime: REPORTS_STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useReportLeadFunnel(workspaceId: string | null, range: ReportRange, locationId?: string) {
  return useQuery({
    queryKey: [workspaceId, 'reports', 'lead-funnel', range, locationId],
    queryFn: () => api.get<LeadFunnelPoint[]>(workspacePath(workspaceId!, '/reports/lead-funnel'), { query: query(range, locationId) }),
    enabled: !!workspaceId,
    staleTime: REPORTS_STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useReportEmployees(workspaceId: string | null, range: ReportRange, locationId?: string) {
  return useQuery({
    queryKey: [workspaceId, 'reports', 'employees', range, locationId],
    queryFn: () => api.get<EmployeePerformancePoint[]>(workspacePath(workspaceId!, '/reports/employees'), { query: query(range, locationId) }),
    enabled: !!workspaceId,
    staleTime: REPORTS_STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useReportCredits(workspaceId: string | null, range: ReportRange, locationId?: string) {
  return useQuery({
    queryKey: [workspaceId, 'reports', 'credits', range, locationId],
    queryFn: () => api.get<CreditUsagePoint[]>(workspacePath(workspaceId!, '/reports/credits'), { query: query(range, locationId) }),
    enabled: !!workspaceId,
    staleTime: REPORTS_STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export async function exportReportExcel(workspaceId: string, report: ReportName, range: ReportRange, locale: string, locationId?: string): Promise<void> {
  const accessToken = await getAccessToken();
  const normalizedLocale = locale.startsWith('zh') ? 'zh-CN' : 'en';
  const res = await fetch(`${import.meta.env.VITE_API_URL as string}${workspacePath(workspaceId, '/reports/export')}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ report, ...range, locationId, locale: normalizedLocale }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { code?: string } } | null;
    throw new Error(body?.error?.code ?? 'EXPORT_FAILED');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const disposition = res.headers.get('content-disposition');
  const match = disposition?.match(/filename="([^"]+)"/);
  a.download = match?.[1] ?? `wrapforce-${report}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
